import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';

import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import type { LoginEduAuthorizationButtonLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { EButtonStyleType } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { SpShadowSettingsComponent } from '@components/sp-shadow-settings/sp-shadow-settings.component';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Styling panel for the "log in" button. Reads and writes `buttons[index]`
 * straight on the widget signal form; the button's own `isActive` flag and its
 * `modalTrigger` are owned by the main panel, so patching field-by-field leaves
 * them untouched. The parent control owns the transaction — save/cancel are
 * emitted up.
 */
@Component({
    selector: 'web-authorization-button-settings',
    templateUrl: './authorization-button-settings.component.html',
    styleUrls: ['./authorization-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpShadowSettingsComponent, WebCustomButtonComponent],
})
export class AuthorizationButtonSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    public readonly EButtonStyleType = EButtonStyleType;
    public readonly ERadioGroupType = ERadioGroupType;

    private readonly button = computed<LoginEduAuthorizationButtonLegacy | undefined>(
        () => this.form().buttons().value()[this.index()] as LoginEduAuthorizationButtonLegacy | undefined
    );

    public readonly label = computed(() => this.button()?.label ?? '');
    public readonly styleType = computed(() => this.button()?.styleType);
    public readonly size = computed(() => this.button()?.size);
    public readonly buttonBase = computed(() => this.button()?.buttonBase);
    public readonly buttonHover = computed(() => this.button()?.buttonHover);
    public readonly isIndependentCorners = computed(() => Boolean(this.button()?.isIndependentCorners));
    public readonly advancedRadius = computed(() => this.button()?.advancedRadius);
    public readonly advancedBorderSettings = computed(() => this.button()?.advancedBorderSettings);
    public readonly isShadow = computed(() => Boolean(this.button()?.isShadow));
    public readonly shadowSettings = computed(() => this.button()?.shadowSettings);
    public readonly isCustomStyle = computed(() => this.styleType() === EButtonStyleType.custom);

    /** Blank button text is invalid — Save and the Enter shortcut are gated on it. */
    public readonly isEmptyLabel = computed(() => isEmptyButtonLabel(this.label()));

    // --- Setters (template event handlers) ---

    public onLabelChange(event: Event): void {
        this.patch({ label: (event.target as HTMLInputElement).value });
    }

    public onStyleTypeChange(event: Event): void {
        this.patch({
            styleType: (event.target as HTMLSelectElement).value as LoginEduAuthorizationButtonLegacy['styleType'],
        });
    }

    public setSize(value: string): void {
        this.patch({ size: value as LoginEduAuthorizationButtonLegacy['size'] });
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<LoginEduAuthorizationButtonLegacy> = {
            buttonBase: changes.base as LoginEduAuthorizationButtonLegacy['buttonBase'],
            buttonHover: changes.hover as LoginEduAuthorizationButtonLegacy['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as LoginEduAuthorizationButtonLegacy['advancedRadius'];
        }
        if (changes?.advancedBorderSettings) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as LoginEduAuthorizationButtonLegacy['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    public setShadow(value: boolean): void {
        this.patch({ isShadow: value });
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.patch({ shadowSettings: settings });
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.isEmptyLabel()) return;

        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }

    private patch(patch: Partial<LoginEduAuthorizationButtonLegacy>): void {
        this.form().updateButton(this.index(), patch);
    }
}
